import { inject } from '@angular/core';
import {
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpResponse,
  HttpErrorResponse,
} from '@angular/common/http';

import { Observable, tap } from 'rxjs';
import { HttpContextToken } from '@angular/common/http';
import { HttpContext } from '@angular/common/http';
import { ToastService } from '../../shared/services/toast.service';

export interface ToastContext {
  message?: string;

  severity?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
}

export const TOAST_CONTEXT = new HttpContextToken<ToastContext>(() => ({}));
const NO_MESSAGE_TOAST_CONTEXT = new HttpContextToken<boolean>(() => false);

export function withNoMessageToast(): HttpContext {
  return new HttpContext().set(NO_MESSAGE_TOAST_CONTEXT, true);
}

export const httpToastInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  const toast = inject(ToastService);
  const context = req.context.get(TOAST_CONTEXT);
  const noMessageToast = req.context.get(NO_MESSAGE_TOAST_CONTEXT);
  if (noMessageToast) {
    return next(req);
  }
  return next(req).pipe(
    tap({
      next: (event) => {
        if (event instanceof HttpResponse) {
          if (['POST', 'PUT', 'DELETE'].includes(req.method)) {
            const message =
              context.message || 'Operation completed successfully!';
            const duration = context.duration || 3000;
            const severity = context.severity || 'success';
            if (severity === 'success')
              toast.success(message, undefined, duration);
            else if (severity === 'info')
              toast.info(message, undefined, duration);
            else if (severity === 'warning')
              toast.warning(message, undefined, duration);
            else if (severity === 'error')
              toast.error(message, undefined, duration);
          }
        }
      },
      error: (_error: HttpErrorResponse) => {
        if (_error.status !== 401) {
          // Use custom message from context if available, otherwise use error message
          const message =
            context.message ||
            _error.error?.message ||
            'Unexpected error occurred';
          const duration = context.duration || 5000;
          const severity = context.severity || 'error';

          if (severity === 'error') toast.error(message, 'Error', duration);
          else if (severity === 'warning')
            toast.warning(message, 'Warning', duration);
          else if (severity === 'info') toast.info(message, 'Info', duration);
          else toast.success(message, 'Success', duration);
        }
      },
    })
  );
};
